/**
 * iter-170ah — API Keys management.
 *
 * Lists the user's keys, lets them mint new ones, and revoke existing
 * ones. The plain key is shown exactly once at creation with a
 * copy-to-clipboard CTA + an explicit "won't be shown again" warning.
 *
 * Lives inside a ``<LockedSection featureKey="basic_api">`` so this
 * component only ever runs for entitled users.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { KeyRound, Plus, Copy, AlertTriangle, CheckCircle2, Loader2, Trash2 } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { api } from "../lib/api";
import { toast } from "sonner";

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(
      lang === "es" ? "es-CO" : lang === "pt" ? "pt-BR" : "en-US",
      { year: "numeric", month: "short", day: "2-digit" },
    );
  } catch {
    return iso;
  }
}

export function ApiKeysManager() {
  const { lang } = useLang();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [revealed, setRevealed] = useState(null);   // last-minted plain key

  const { data, isLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: async () => (await api.get("/api-keys")).data,
    retry: false,
  });
  const items = data?.items || [];

  const createMut = useMutation({
    mutationFn: async () =>
      (await api.post("/api-keys", { name: name.trim() })).data,
    onSuccess: (d) => {
      setRevealed(d.key);
      setName("");
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      toast.success(tx(lang, "API key created", "API key creada", "API key criada"));
    },
    onError: () => toast.error(tx(lang, "Create failed", "Error al crear", "Erro ao criar")),
  });

  const revokeMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/api-keys/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      toast.success(tx(lang, "Key revoked", "Key revocada", "Key revogada"));
    },
    onError: () => toast.error(tx(lang, "Revoke failed", "Error al revocar", "Erro ao revogar")),
  });

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(tx(lang, "Copied", "Copiado", "Copiado"));
    } catch {
      toast.error(tx(lang, "Copy failed", "No se pudo copiar", "Falha ao copiar"));
    }
  };

  return (
    <Card className="p-5 rounded-2xl space-y-4" data-testid="api-keys-manager">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-slate-700" />
        <h3 className="font-semibold text-slate-900">
          {tx(lang, "API keys", "API keys", "API keys")}
        </h3>
      </div>

      {/* One-time reveal banner */}
      {revealed && (
        <div
          className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3"
          data-testid="api-keys-revealed-banner"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-900 leading-snug">
              {tx(lang,
                "Save this key now — it won't be shown again. If you lose it, revoke and create a new one.",
                "Guarda esta key ahora — no se mostrará otra vez. Si la pierdes, revócala y crea una nueva.",
                "Guarde esta key agora — não será mostrada de novo. Se perdê-la, revogue e crie outra.")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <code
              className="flex-1 text-xs font-mono bg-white border border-amber-200 px-3 py-2 rounded break-all"
              data-testid="api-keys-revealed-value"
            >
              {revealed}
            </code>
            <Button
              size="sm"
              onClick={() => copyToClipboard(revealed)}
              className="bg-amber-600 hover:bg-amber-700 gap-1.5"
              data-testid="api-keys-revealed-copy"
            >
              <Copy className="h-3.5 w-3.5" /> {tx(lang, "Copy", "Copiar", "Copiar")}
            </Button>
          </div>
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            onClick={() => setRevealed(null)}
            className="text-[11px] text-amber-700 hover:text-amber-900 underline"
            data-testid="api-keys-revealed-dismiss"
          >
            {tx(lang, "I saved it — close", "Ya la guardé — cerrar", "Já salvei — fechar")}
          </button>
        </div>
      )}

      {/* Create new */}
      <div className="flex gap-2">
        <Input
          placeholder={tx(lang, "Key name (e.g. 'Production CI')",
            "Nombre de la key (ej: 'CI producción')",
            "Nome da key (ex: 'CI produção')")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={64}
          data-testid="api-keys-new-name"
          className="flex-1"
        />
        <Button
          onClick={() => createMut.mutate()}
          disabled={!name.trim() || createMut.isPending}
          className="gap-1.5"
          data-testid="api-keys-create-btn"
        >
          {createMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          {tx(lang, "Create", "Crear", "Criar")}
        </Button>
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-xs text-slate-400 italic">
          {tx(lang, "Loading…", "Cargando…", "Carregando…")}
        </p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-6 text-center" data-testid="api-keys-empty">
          {tx(lang, "No keys yet.", "Aún no tienes keys.", "Ainda sem keys.")}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="api-keys-list">
          {items.map((k) => {
            const isRevoked = !!k.revoked_at;
            return (
              <li
                key={k.id}
                className={`flex items-center gap-3 p-3 rounded-xl border ${
                  isRevoked ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"
                }`}
                data-testid={`api-keys-row-${k.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">{k.name}</span>
                    {isRevoked && (
                      <Badge variant="destructive" className="text-[10px]">
                        {tx(lang, "Revoked", "Revocada", "Revogada")}
                      </Badge>
                    )}
                  </div>
                  <code className="text-[11px] font-mono text-slate-500 block mt-0.5">{k.prefix}…</code>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {tx(lang, "Created", "Creada", "Criada")} {fmtDate(k.created_at, lang)}
                    {k.last_used_at && (
                      <> · {tx(lang, "Last used", "Último uso", "Último uso")} {fmtDate(k.last_used_at, lang)}</>
                    )}
                  </p>
                </div>
                {isRevoked ? (
                  <CheckCircle2 className="h-4 w-4 text-slate-400" />
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => revokeMut.mutate(k.id)}
                    disabled={revokeMut.isPending}
                    className="gap-1.5 rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
                    data-testid={`api-keys-revoke-${k.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                    {tx(lang, "Revoke", "Revocar", "Revogar")}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
